/**
 * Generates and renders deterministic 16×16 author avatars + usernames for
 * the Variant-B UGC features (shop offer authors, per-level "created by"
 * stamps). Same seed → same identity, cached for the session.
 *
 * Why 16×16 with structural rules: pure noise at that resolution reads as
 * static. We instead split the avatar into rows of "features" (hair, eyes,
 * mouth, shoulders), each driven by a small bank of templates, and mirror
 * the left half to the right. Outputs read as faces, not patterns.
 */

import Phaser from "phaser";

import type { AuthorIdentity, AuthorProfile } from "../types";
import { LanguageService } from "../i18n";

import {
  AUTHOR_BIO_TAGLINE_COUNT,
  AUTHOR_BIO_TAG_COUNT,
  authorBioPools,
} from "./authorBioPool";
import { AUTHOR_NAME_POOL } from "./authorNamePool";
import { hashString, makeSeededRng } from "./seededRng";

const AVATAR_SIZE = 16;

/** Member-since pick range = 64 months from 2021-01 (inclusive) to 2026-04. */
const MEMBER_SINCE_START_YEAR = 2021;
const MEMBER_SINCE_MONTH_COUNT = 64;

/** Curated palette per role. Index 0 of each row is always "transparent". */
const SKIN_TONES = [0xf3c79c, 0xd29c6e, 0x8a5a3b, 0x5b3a26, 0xc7b1d4, 0x9ec46e];
const HAIR_COLORS = [0x1a1a1a, 0x4a2e1c, 0xc87037, 0xd8c46c, 0x6b8eaa, 0xb1454b, 0xf4ead0];
const ACCENT_COLORS = [0x6a4a2a, 0x3a4860, 0x7a3a3a, 0x2f5a3f, 0x6b3a6b, 0x2a2a2a];

type HeadShape = "round" | "square" | "pointed";
type HairStyle = "short" | "long" | "hood" | "horned" | "bald";
type EyeStyle = "dot" | "dash" | "round";

const HEAD_SHAPES: readonly HeadShape[] = ["round", "square", "pointed"];
const HAIR_STYLES: readonly HairStyle[] = ["short", "long", "hood", "horned", "bald"];
const EYE_STYLES: readonly EyeStyle[] = ["dot", "dash", "round"];

export class AuthorIdentityService {
  private readonly cache = new Map<number, AuthorIdentity>();
  /** Key = `${seed}:${lang}` so DE / EN profiles co-exist if the language
   *  changes mid-session (each maps to the same logical bio index). */
  private readonly profileCache = new Map<string, AuthorProfile>();

  forSeed(seed: number): AuthorIdentity {
    const key = seed >>> 0;
    const hit = this.cache.get(key);
    if (hit) return hit;
    const identity = this.generate(key);
    this.cache.set(key, identity);
    return identity;
  }

  /**
   * Builds (and memoises) the richer profile shown by the hover/click
   * tooltip — tagline, tags, plausible counters, member-since month.
   *
   * Bio selection is split into two stages so the picks track language:
   * the RNG (decoupled from the avatar RNG via `0xb10b10`) picks INDICES,
   * and the active-language pool resolves each index to its text. Same
   * seed → same indices → same logical bio in either language. The cache
   * key includes language so re-asking after a language switch returns a
   * freshly-resolved profile.
   */
  profileForSeed(seed: number): AuthorProfile {
    const seedKey = seed >>> 0;
    const lang = LanguageService.getLanguage();
    const cacheKey = `${seedKey}:${lang}`;
    const hit = this.profileCache.get(cacheKey);
    if (hit) return hit;
    const identity = this.forSeed(seedKey);
    const rng = makeSeededRng(seedKey ^ 0xb10b10);
    const pools = authorBioPools(lang);

    const taglineIdx = rng.intRange(0, AUTHOR_BIO_TAGLINE_COUNT);
    const tagline = pools.taglines[taglineIdx]!;

    const tagCount = rng.chance(0.6) ? 1 : 2;
    const tagIndices: number[] = [];
    while (tagIndices.length < tagCount) {
      const idx = rng.intRange(0, AUTHOR_BIO_TAG_COUNT);
      if (!tagIndices.includes(idx)) tagIndices.push(idx);
    }
    const tags = tagIndices.map((i) => pools.tags[i]!);

    const levelsAuthored = rng.chance(0.7) ? rng.intRange(1, 30) : rng.intRange(30, 200);
    const tradesCompleted = rng.chance(0.7) ? rng.intRange(0, 100) : rng.intRange(100, 2000);

    // Member-since: 2021-01 .. 2026-04 → 64-month uniform pick.
    const monthsFromStart = rng.intRange(0, MEMBER_SINCE_MONTH_COUNT);
    const memberSinceYear = MEMBER_SINCE_START_YEAR + Math.floor(monthsFromStart / 12);
    const memberSinceMonth = (monthsFromStart % 12) + 1;

    const profile: AuthorProfile = {
      ...identity,
      tagline,
      tags,
      levelsAuthored,
      tradesCompleted,
      memberSinceYear,
      memberSinceMonth,
    };
    this.profileCache.set(cacheKey, profile);
    return profile;
  }

  private generate(seed: number): AuthorIdentity {
    const rng = makeSeededRng(seed);
    const username = AUTHOR_NAME_POOL[seed % AUTHOR_NAME_POOL.length]!;

    // Palette: [transparent, skin, hair, accent].
    const skin = rng.pick(SKIN_TONES);
    const hair = rng.pick(HAIR_COLORS);
    const accent = rng.pick(ACCENT_COLORS);
    const palette = [0x000000, skin, hair, accent];

    const headShape = rng.pick(HEAD_SHAPES);
    const hairStyle = rng.pick(HAIR_STYLES);
    const eyeStyle = rng.pick(EYE_STYLES);

    const pixels = new Uint8Array(AVATAR_SIZE * AVATAR_SIZE);
    // Left half = cols 0..7, mirrored to cols 8..15.
    // Plot to the left half, then mirror on the way out.

    const set = (col: number, row: number, idx: number): void => {
      if (col < 0 || col > 7 || row < 0 || row >= AVATAR_SIZE) return;
      pixels[row * AVATAR_SIZE + col] = idx;
      pixels[row * AVATAR_SIZE + (AVATAR_SIZE - 1 - col)] = idx;
    };

    // Head outline (rows 5..11, skin filled).
    for (let row = 5; row <= 11; row++) {
      const rowFromTop = row - 5;
      let leftEdge = 1;
      if (headShape === "round") {
        if (rowFromTop === 0 || rowFromTop === 6) leftEdge = 2;
        else leftEdge = 1;
      } else if (headShape === "square") {
        leftEdge = 1;
      } else {
        leftEdge = rowFromTop < 3 ? 2 : 1;
      }
      for (let col = leftEdge; col <= 7; col++) set(col, row, 1);
    }

    // Hair / hat (rows 2..5 typically).
    if (hairStyle === "short") {
      for (let row = 3; row <= 5; row++) {
        for (let col = 2; col <= 7; col++) set(col, row, 2);
      }
    } else if (hairStyle === "long") {
      for (let row = 3; row <= 5; row++) {
        for (let col = 2; col <= 7; col++) set(col, row, 2);
      }
      // Long hair down the side rows 6..10.
      for (let row = 6; row <= 10; row++) set(1, row, 2);
    } else if (hairStyle === "hood") {
      for (let row = 2; row <= 5; row++) {
        for (let col = 1; col <= 7; col++) set(col, row, 3);
      }
      // Hood extends down each side.
      for (let row = 6; row <= 11; row++) set(1, row, 3);
    } else if (hairStyle === "horned") {
      for (let row = 4; row <= 5; row++) {
        for (let col = 2; col <= 7; col++) set(col, row, 2);
      }
      // Single horn rising from the top.
      set(7, 3, 2);
      set(7, 2, 2);
      set(6, 2, 2);
    } else {
      // bald: skip top hair, leave skin.
    }

    // Eyes (rows 7..8 typically, around col 3-4 in left half = mirrors to col 11-12).
    if (eyeStyle === "dot") {
      set(3, 8, 0);
    } else if (eyeStyle === "dash") {
      set(3, 8, 0);
      set(4, 8, 0);
    } else {
      // round: 2x2
      set(3, 7, 0);
      set(4, 7, 0);
      set(3, 8, 0);
      set(4, 8, 0);
    }

    // Mouth (row 10, a single accent pixel at col 5 mirrors to col 10 = mouth bar of 2 px).
    set(5, 10, 3);
    set(6, 10, 3);

    // Shoulders + neck (rows 12..14, accent).
    for (let row = 12; row <= 14; row++) {
      const fromEdge = row === 12 ? 2 : row === 13 ? 1 : 0;
      for (let col = fromEdge; col <= 7; col++) set(col, row, 3);
    }

    return {
      username,
      avatarPixels: pixels,
      avatarPalette: palette,
    };
  }

  /**
   * Render an avatar cropped to a circle, sized to `displaySize` pixels.
   * Pixels outside the inscribed circle are dropped on the way to the
   * canvas, so the resulting Image sits cleanly inside a UI frame without
   * needing a runtime mask. Cached per (username, size). Returns the image
   * positioned at (x, y) with origin (0, 0).
   */
  drawCircleAvatar(
    scene: Phaser.Scene,
    x: number,
    y: number,
    identity: AuthorIdentity,
    displaySize: number
  ): Phaser.GameObjects.Image {
    const key = `avatarCircle:${identity.username}:${displaySize}`;
    if (!scene.textures.exists(key)) {
      const tex = scene.textures.createCanvas(key, displaySize, displaySize);
      if (!tex) {
        return scene.add.rectangle(x, y, displaySize, displaySize, 0x000000) as unknown as Phaser.GameObjects.Image;
      }
      const ctx = tex.getContext();
      const cell = displaySize / AVATAR_SIZE;
      const radius = displaySize / 2;
      const cx = radius;
      const cy = radius;
      const radiusSq = radius * radius;
      for (let row = 0; row < AVATAR_SIZE; row++) {
        for (let col = 0; col < AVATAR_SIZE; col++) {
          const idx = identity.avatarPixels[row * AVATAR_SIZE + col]!;
          if (idx === 0) continue;
          const px = cell * (col + 0.5);
          const py = cell * (row + 0.5);
          if ((px - cx) * (px - cx) + (py - cy) * (py - cy) > radiusSq) continue;
          const rgb = identity.avatarPalette[idx]!;
          ctx.fillStyle = `#${rgb.toString(16).padStart(6, "0")}`;
          ctx.fillRect(px - cell / 2, py - cell / 2, cell, cell);
        }
      }
      tex.refresh();
    }
    return scene.add.image(x, y, key).setOrigin(0, 0);
  }

  /**
   * Render an avatar into the scene at (x, y) with the given integer pixel
   * scale. Returns the created Image so the caller can position/destroy it.
   * Identity textures are cached per (seed, scale).
   */
  drawAvatar(
    scene: Phaser.Scene,
    x: number,
    y: number,
    identity: AuthorIdentity,
    scale = 1
  ): Phaser.GameObjects.Image {
    // Use textureKey directly — but we need a stable seed reference. The
    // cleanest approach is to embed a seed hash into the texture key.
    // Since AuthorIdentity doesn't carry the seed, we derive a content-hash
    // from its username + first few pixels (cheap and stable).
    const key = `avatar:${identity.username}:${scale}`;
    if (!scene.textures.exists(key)) {
      const tex = scene.textures.createCanvas(key, AVATAR_SIZE * scale, AVATAR_SIZE * scale);
      if (!tex) {
        // Phaser failed to allocate canvas; fall back to a 1px placeholder.
        return scene.add.rectangle(x, y, 16 * scale, 16 * scale, 0x000000) as unknown as Phaser.GameObjects.Image;
      }
      const ctx = tex.getContext();
      for (let row = 0; row < AVATAR_SIZE; row++) {
        for (let col = 0; col < AVATAR_SIZE; col++) {
          const idx = identity.avatarPixels[row * AVATAR_SIZE + col]!;
          if (idx === 0) continue;
          const rgb = identity.avatarPalette[idx]!;
          ctx.fillStyle = `#${rgb.toString(16).padStart(6, "0")}`;
          ctx.fillRect(col * scale, row * scale, scale, scale);
        }
      }
      tex.refresh();
    }
    return scene.add.image(x, y, key).setOrigin(0, 0);
  }
}

/** Seed for the player's own main-run author stamp. Stable per session. */
export function mainLevelAuthorSeed(participantId: string, sessionStartMs: number): number {
  return hashString(`main:${participantId}:${sessionStartMs}`);
}

/** Seed for a sub-level author. Stable per (depth, biome, level seed). */
export function subLevelAuthorSeed(depth: number, biome: string, levelSeed: number): number {
  return hashString(`sub:${depth}:${biome}:${levelSeed}`);
}

/** Seed for an offer author. Each offer gets its own author. */
export function offerAuthorSeed(offerId: string): number {
  return hashString(`offer:${offerId}`);
}
